"use client";

import { LogOut, Eye, EyeOff, SendToBackIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemLink,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  dropdownTriggerVariants
} from "@gettactic/components/src/@v2/components/ui/dropdown-menu";
import DesignSwitcher from "@/components/design/DesignSwitcher";
import { useIsMobile } from "@v2/hooks/use-mobile";
import React, { memo, useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { canScheduleOthers, cn, useDisclosure } from "@gettactic/helpers";
import { useImpersonateContext } from "@webV2/providers/ImpersonateProvider";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { invokeMobileAppAction } from "@gettactic/helpers/src/webview/invokeMobileAppAction";
import { ZendeskChat, ZendeskChatRef } from "@gettactic/helpers/src/ZendeskChat";

export const navUserClassNames = {
  MenuItem:
    "cursor-pointer hover:rounded-none active:rounded-none transition-none",
  MenuItemHiddenOnMobile: "hidden md:block"
};
import { AuthenticatedUser } from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { api } from "@/lib/api/api";
import { OrganizationSwitcher } from "@v2/components/organizations/OrganizationSwitcher/OrganizationSwitcher";
import { authenticatedUserIsInMoreOrganizations } from "@gettactic/helpers/src/features/authenticatedUserIsInMoreOrganizations";
import { toast } from "react-hot-toast";

export interface NavUserProps {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  trigger?: React.ReactNode;
}

const hamburgerMenuItems = [
  {
    id: "settings",
    title: "settings",
    href: "/settings/profile",
    enabled: (authenticatedUser: AuthenticatedUser) => true,
    showOnMobile: false
  },
  {
    id: "admin",
    title: "admin",
    href: "/admin",
    enabled: (authenticatedUser: AuthenticatedUser) =>
      canScheduleOthers(authenticatedUser),
    showOnMobile: false
  },
  {
    id: "chatSupport",
    title: "chatSupport",
    href: null,
    enabled: (authenticatedUser: AuthenticatedUser) => true,
    showOnMobile: false,
    isAction: true
  },
  {
    id: "billing",
    title: "billing",
    href: null,
    enabled: (authenticatedUser: AuthenticatedUser) =>
      authenticatedUser &&
      (authenticatedUser.user?.permissions?.includes("billing:read") ?? false),
    showOnMobile: false,
    isAction: true
  },
  {
    id: "helpCenter",
    title: "helpCenter",
    href: "https://help.gettactic.com/hc/en-us",
    enabled: (authenticatedUser: AuthenticatedUser) => true,
    showOnMobile: false
  }
];

const checkIfMenuItemIsEnabled = (
  item: (typeof hamburgerMenuItems)[number],
  authenticatedUser: AuthenticatedUser
) => {
  if (typeof item.enabled === "function") {
    return item.enabled(authenticatedUser);
  }
  return !!item.enabled;
};

export const NavUser: React.FC<NavUserProps> = ({ user, trigger }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation("common");
  const {
    canImpersonate,
    isBeingImpersonated,
    toggleSidebarButton,
    isSidebarButtonEnabled,
    setIsWidgetVisible,
    stopImpersonating
  } = useImpersonateContext();
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);
  const organizationSwitcherDisclosure = useDisclosure();

  const zendeskChatRef = useRef<ZendeskChatRef>(null);
  const isMobileApp = isTacticAppWebView();
  const [isOpen, setIsOpen] = useState(false);

  // Use ref to track current state to avoid closure issues
  const buttonEnabledRef = useRef(isSidebarButtonEnabled);

  // Update ref when state changes
  useEffect(() => {
    buttonEnabledRef.current = isSidebarButtonEnabled;
  }, [isSidebarButtonEnabled]);

  const Avatar = memo(() => (
    <UserAvatar
      user={{
        name: user.name,
        profile_photo_url: user.avatar
      }}
      size="md"
    />
  ));

  const mobileTrigger = (
    <div className={"relative"}>
      {trigger ? (
        <>
          {trigger}
          <span
            className={
              "-top-4 -right-4 absolute block scale-[0.4] overflow-hidden rounded-full border-4 border-white"
            }
          >
            <Avatar />
          </span>
        </>
      ) : (
        <Avatar />
      )}
    </div>
  );

  if (isMobileApp) {
    return (
      <button
        className={"relative"}
        onClick={() => {
          invokeMobileAppAction("openInfo", {
            organizations: authenticatedUser.organizations,
            organization: authenticatedUser?.organization
          });
        }}
      >
        {mobileTrigger}
      </button>
    );
  }

  const inMoreOrganizations =
    authenticatedUserIsInMoreOrganizations(authenticatedUser);

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger className={dropdownTriggerVariants.rounded}>
          {mobileTrigger}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg p-0"
          variant="dropdown"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={4}
        >
          <DropdownMenuItemLink
            href="/settings/profile"
            className={navUserClassNames.MenuItem}
            onClick={() => setIsOpen(false)}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
          </DropdownMenuItemLink>
          {!isMobile && <Separator className="shadow-sm" />}
          <DropdownMenuGroup>
            {hamburgerMenuItems.map((item) => {
              if (!checkIfMenuItemIsEnabled(item, authenticatedUser)) return;

              if (item.isAction && item.id === "chatSupport") {
                return (
                  <DropdownMenuItem
                    key={item.id}
                    className={cn(
                      navUserClassNames.MenuItem,
                      !item.showOnMobile &&
                        navUserClassNames.MenuItemHiddenOnMobile,
                      "flex w-full items-center justify-between"
                    )}
                    onClick={() => {
                      zendeskChatRef.current?.openChat();
                      setIsOpen(false);
                    }}
                  >
                    {t(`navActions.${item.title}`)}
                  </DropdownMenuItem>
                );
              }
              if (item.isAction && item.id === "billing") {
                return (
                  <DropdownMenuItem
                    key={item.id}
                    className={cn(
                      navUserClassNames.MenuItem,
                      !item.showOnMobile &&
                        navUserClassNames.MenuItemHiddenOnMobile,
                      "flex w-full items-center justify-between"
                    )}
                    onClick={async () => {
                      try {
                        const response = await api.client.billing.getPortalUrl();
                        if (response.result?.url) {
                          window.open(response.result.url, "_blank");
                        }
                      } catch (error) {
                        toast.error("Failed to navigate to the billing portal");
                        console.error("Failed to get billing portal URL:", error);
                      }
                      setIsOpen(false);
                    }}
                  >
                    {t(`navActions.${item.title}`)}
                  </DropdownMenuItem>
                );
              }
              return (
                <DropdownMenuItemLink
                  key={item.id}
                  href={item.href || undefined}
                  className={cn(
                    navUserClassNames.MenuItem,
                    !item.showOnMobile &&
                      navUserClassNames.MenuItemHiddenOnMobile
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  {t(`navActions.${item.title}`)}
                </DropdownMenuItemLink>
              );
            })}
          </DropdownMenuGroup>

          <>
            {!isMobileApp && (
              <>
                <Separator />
                <DesignSwitcher isNewMenu={true} />
              </>
            )}
            {(canImpersonate || isBeingImpersonated) && (
              <div className="hidden items-center justify-between pr-2 md:flex">
                <DropdownMenuItem
                  className={cn(
                    navUserClassNames.MenuItem,
                    "flex-1",
                    isBeingImpersonated && "text-red-500"
                  )}
                  onClick={() => {
                    toggleSidebarButton();
                    setTimeout(() => {
                      // wait a sec to make sure the button is visible
                      // we want it to position the widget correctly

                      if (buttonEnabledRef.current) {
                        setIsWidgetVisible(true);
                      }
                    }, 200);
                    setIsOpen(false);
                  }}
                >
                  {isSidebarButtonEnabled ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  {t("navActions.impersonate")}
                </DropdownMenuItem>
              </div>
            )}
            {isBeingImpersonated && (
              <DropdownMenuItem
                className={cn(
                  navUserClassNames.MenuItem,
                  "flex-1",
                  isBeingImpersonated && "text-red-500"
                )}
                onClick={() => {
                  stopImpersonating();
                  setIsOpen(false);
                }}
              >
                <div className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 p-1">
                  <div className="flex h-2 w-2 items-center justify-center rounded-[3px] bg-white p-1" />
                </div>
                {t("navActions.stopImpersonating")}
              </DropdownMenuItem>
            )}
            <Separator />
          </>

          {inMoreOrganizations && isMobile && (
            <>
              <DropdownMenuItemLink
                onClick={() => {
                  organizationSwitcherDisclosure.open();
                  setIsOpen(false);
                }}
                className={navUserClassNames.MenuItem}
              >
                <SendToBackIcon className="h-4 w-4" />
                Switch organization
              </DropdownMenuItemLink>
              <Separator />
            </>
          )}

          {isMobileApp && (
            <>
              <DropdownMenuItemLink
                onClick={() => {
                  invokeMobileAppAction("openInfo", {
                    organizations: authenticatedUser.organizations,
                    organization: authenticatedUser?.organization
                  });
                  setIsOpen(false);
                }}
                className={navUserClassNames.MenuItem}
              >
                Info
              </DropdownMenuItemLink>
              <Separator />
            </>
          )}

          {isMobileApp ? (
            <DropdownMenuItemLink
              className={navUserClassNames.MenuItem}
              onClick={() => {
                invokeMobileAppAction("logout", undefined);
                setIsOpen(false);
              }}
            >
              <LogOut />
              {t("navActions.logout")}
            </DropdownMenuItemLink>
          ) : (
            <DropdownMenuItemLink
              className={navUserClassNames.MenuItem}
              href={`/auth/logout?returnUrl=${encodeURIComponent(
                `${window.location.origin}?logout=true`
              )}`}
              onClick={() => setIsOpen(false)}
            >
              <LogOut />
              {t("navActions.logout")}
            </DropdownMenuItemLink>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isMobile && inMoreOrganizations && (
        <OrganizationSwitcher
          isOpen={organizationSwitcherDisclosure.isOpen}
          onOpenChange={organizationSwitcherDisclosure.onOpenChange}
        />
      )}
      <ZendeskChat ref={zendeskChatRef} />
    </>
  );
};

const Separator = ({ className }: { className?: string }) => (
  <DropdownMenuSeparator className={cn("m-0", className)} />
);
